// Copyright (c) Ping. All rights reserved.
//
// Web host for shared NewGroupModal.

import { useCallback, useEffect, useState } from 'react';

import {
  type NewChatModalDirectoryUser,
  NewGroupModal as NewGroupModalUi,
  useSession,
} from '@ping/ui';

import { dataUrlToBlob } from '@core/dataUrl';
import { log } from '@core/log/log';
import { ApiNetworkError, type DirectoryUser, lookupUser } from '@core/network';
import { searchUsers } from '@core/network/users';
import { retrieveIdentity } from '@core/persistence/identity';
import {
  type DeviceContact,
  filterDeviceContacts,
  loadDeviceContacts,
} from '@features/contacts/deviceContacts';
import { useSelfProfile } from '@features/profile/selfProfileStore';
import {
  type AvatarContentType,
  requestAvatarUploadUrl,
  uploadAvatarBytes,
} from '@features/settings/web/profilesApi';

import {
  createWebGroupConversation,
  GroupCreateError,
} from './createWebGroupConversation';
import { pickAvatarDataUrl } from './pickAvatarDataUrl';

/**
 * Upload a (data-URL) group avatar to the media server and return its media id
 * (`blob_key`), resolvable by any device via `GET /v1/media/{id}`. Best-effort:
 * returns `undefined` on any failure so group creation never blocks on the
 * avatar — the data URL still rides the local row + hydration broadcast.
 */
async function uploadGroupAvatarDataUrl(dataUrl: string): Promise<string | undefined> {
  try {
    // Decode in-process — `fetch('data:…')` is blocked by the CSP `connect-src`
    // directive (no `data:` scheme), which silently failed the upload and left
    // the group with no avatar. See `core/dataUrl.ts`.
    const blob = dataUrlToBlob(dataUrl);
    const contentType: AvatarContentType =
      blob.type === 'image/webp' ? 'image/webp' : 'image/jpeg';
    const upload = await requestAvatarUploadUrl(contentType);
    await uploadAvatarBytes(upload, blob);
    return upload.blobKey;
  } catch (err) {
    log.warn({ event: 'newGroup.avatarUpload.failed', error: (err as Error).message });
    return undefined;
  }
}

function looksLikeContact(q: string): boolean {
  return /@/.test(q) || /^\+?[0-9][0-9 ()-]{5,}$/.test(q);
}

function contactToDirectoryUser(c: DeviceContact): NewChatModalDirectoryUser {
  return {
    id: `contact:${c.id}`,
    username: c.identifier,
    displayName: c.displayName,
    source: 'contact',
    ...(c.avatarUri !== undefined ? { avatarUri: c.avatarUri } : {}),
  };
}

export interface NewGroupModalProps {
  readonly open: boolean;
  readonly onDismiss: () => void;
  readonly onConversationStarted: (conversationId: string) => void;
}

function formatCreateError(err: unknown): string {
  if (err instanceof GroupCreateError) {
    return err.message;
  }
  if (err instanceof ApiNetworkError) {
    const body = err.serverMessage;
    if (body !== undefined && body.length > 0) return body;
  }
  if (err instanceof Error && err.message.length > 0) {
    return err.message;
  }
  return "Couldn't create that group. Try again.";
}

export function NewGroupModal(props: NewGroupModalProps): JSX.Element {
  const { open, onDismiss, onConversationStarted } = props;
  const { client } = useSession();
  const [deviceContacts, setDeviceContacts] = useState<readonly DeviceContact[]>([]);
  const [groupAvatarUri, setGroupAvatarUri] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (open) {
      setDeviceContacts(loadDeviceContacts());
    } else {
      setGroupAvatarUri(undefined);
    }
  }, [open]);

  const handlePickGroupAvatar = useCallback(() => {
    void pickAvatarDataUrl().then((uri) => {
      if (uri !== null) setGroupAvatarUri(uri);
    });
  }, []);

  const handleSearch = useCallback(
    async (query: string, signal: AbortSignal): Promise<readonly NewChatModalDirectoryUser[]> => {
      const trimmed = query.trim();
      const contactRows = filterDeviceContacts(deviceContacts, trimmed).map(
        contactToDirectoryUser,
      );
      if (trimmed.length === 0) return contactRows;
      const [dir, exact] = await Promise.all([
        searchUsers({ query: trimmed, limit: 30, signal, skipReauth: true })
          .then((r) => r.users)
          .catch(() => [] as DirectoryUser[]),
        looksLikeContact(trimmed)
          ? lookupUser({ identifier: trimmed, signal })
              .then((r) => (r.user !== null ? [r.user] : []))
              .catch(() => [] as DirectoryUser[])
          : Promise.resolve([] as DirectoryUser[]),
      ]);
      const byId = new Map<string, NewChatModalDirectoryUser>();
      for (const c of contactRows) byId.set(c.id, c);
      for (const u of [...exact, ...dir]) {
        if (byId.has(u.id)) continue;
        byId.set(u.id, { ...(u as NewChatModalDirectoryUser), source: 'on-ping' });
      }
      return Array.from(byId.values());
    },
    [deviceContacts],
  );

  const handleCreate = useCallback(
    async (params: {
      readonly title: string;
      readonly members: readonly NewChatModalDirectoryUser[];
      readonly avatarUri?: string;
    }): Promise<string> => {
      if (client === null) {
        throw new Error('Still signing you in. Try again in a moment.');
      }

      const identity = await retrieveIdentity();
      // Resolve any "YOUR CONTACTS" members (email/phone) to Ping accounts;
      // skip those not on Ping yet. Directory members already carry an
      // account id.
      const resolved = await Promise.all(
        params.members.map(async (m) => {
          if (m.source !== 'contact') {
            return { accountId: m.id, displayName: m.displayName };
          }
          try {
            const { user } = await lookupUser({ identifier: m.username });
            return user !== null
              ? { accountId: user.id, displayName: user.displayName }
              : null;
          } catch {
            return null;
          }
        }),
      );
      const members = resolved
        .filter((m): m is { accountId: string; displayName: string } => m !== null)
        .filter((m) => m.accountId !== identity?.accountId);

      // Upload the avatar to media FIRST so its media id can be embedded in the
      // group meta (GroupContext) — making the avatar propagate to every joining
      // device via group state, not just a hydration broadcast.
      const avatarMediaId =
        params.avatarUri !== undefined
          ? await uploadGroupAvatarDataUrl(params.avatarUri)
          : undefined;

      try {
        const result = await createWebGroupConversation({
          client: client as unknown as Parameters<
            typeof createWebGroupConversation
          >[0]['client'],
          members,
          ...(params.title.length > 0 ? { displayName: params.title } : {}),
          ...(params.avatarUri !== undefined ? { avatarUri: params.avatarUri } : {}),
          ...(avatarMediaId !== undefined ? { avatarMediaId } : {}),
        });
        log.info({
          event: 'newGroup.create.ok',
          conversationIdPrefix: result.conversationIdHex.slice(0, 8),
          deduped: result.deduped,
        });
        return result.conversationIdHex;
      } catch (err) {
        throw new Error(formatCreateError(err), { cause: err });
      }
    },
    [client],
  );

  // Render the current user's own avatar on the pinned "You" chip.
  const selfAvatarUrl = useSelfProfile((s) => s.avatarUrl);

  return (
    <NewGroupModalUi
      open={open}
      onDismiss={onDismiss}
      onSearch={handleSearch}
      onCreate={handleCreate}
      onConversationStarted={onConversationStarted}
      onPickGroupAvatar={handlePickGroupAvatar}
      {...(groupAvatarUri !== undefined ? { groupAvatarUri } : {})}
      {...(selfAvatarUrl ? { selfAvatarUri: selfAvatarUrl } : {})}
    />
  );
}
